import React, {useCallback, useState} from "react";
import { makeStyles } from "@mui/styles";
import { useFormik } from "formik";
import { AppField } from "../ui/AppField";
import { AppButton } from "../ui/AppButton";
import {object, string} from "yup";
import {useI18n} from "../../i18n/I18nContext";
import {showError} from "../../utils/NotificationUtils";
import {useIntegrationsContext} from "../../api/integrations/IntegrationsContext";
import {useHistory} from "react-router";
import {Routes} from "../../constants/Routes";

const validationSchema = (translate: any) => object({
    name: string().required(translate("REQUIRED")),
    description: string().max(140).required(translate("REQUIRED")),
    logoUrl: string().url(),
    homePage: string(),
});

const useStyles = makeStyles(() => ({
    root: {
        background: "#FFFFFF",
    },
    fieldRoot: {
        marginBottom: 24
    }
}));

export function IntegrationCreateForm() {
    const classes = useStyles();
    const {translate} = useI18n();
    const {IntegrationsApi} = useIntegrationsContext();
    const history = useHistory();
    const [submitting, setSubmitting] = useState(false);

    const submitHandler = useCallback((values) => {
        setSubmitting(true);
        IntegrationsApi.createIntegration({data: values}).then(res => {
            setSubmitting(false);
            history.replace({pathname: "/integration/editor/" + res.payload.id + "/authentication"})
        }).catch(err => {
            setSubmitting(false);
            showError(err);
        })
    }, [IntegrationsApi, history]);

    const formik = useFormik({
        initialValues: {
            name: "",
            homePage: "",
            description: "",
            logoUrl: "",
        },
        validationSchema: validationSchema(translate),
        onSubmit: (values) => submitHandler(values),
    });
    return (
        <div className={classes.root}>
            <form onSubmit={formik.handleSubmit}>
                <div className={classes.fieldRoot}>
                    <AppField
                        fullWidth
                        required={true}
                        variant="outlined"
                        labelText="Name"
                        name="name"
                        id="name"
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        helpDesc="The name of your app as it should display in Tact."
                    />
                </div>
                <div className={classes.fieldRoot}>
                    <AppField
                        fullWidth
                        required={true}
                        labelText="Description"
                        name="description"
                        variant="outlined"
                        id="description"
                        limit={140}
                        valueLength={formik.values.description?.length}
                        error={
                            formik.touched.description && Boolean(formik.errors.description)
                        }
                        helperText={formik.touched.description && formik.errors.description}
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        helpDesc='A sentence describing your app in 140 characters or less, e.g. "Trello is a team collaboration tool to organize tasks and keep projects on track."'
                    />
                </div>
                <div className={classes.fieldRoot}>
                    <AppField
                        fullWidth
                        labelText="Logo Url"
                        name="logoUrl"
                        variant="outlined"
                        id="logoUrl"
                        error={formik.touched.logoUrl && Boolean(formik.errors.logoUrl)}
                        helperText={formik.touched.logoUrl && formik.errors.logoUrl}
                        value={formik.values.logoUrl}
                        onChange={formik.handleChange}
                        helpDesc="The Logo URL of your app."
                    />
                </div>

                <div className={classes.fieldRoot}>
                    <AppField
                        fullWidth
                        labelText="Home Page Url"
                        name="homePage"
                        variant="outlined"
                        id="homePage"
                        error={formik.touched.homePage && Boolean(formik.errors.homePage)}
                        helperText={formik.touched.homePage && formik.errors.homePage}
                        value={formik.values.homePage}
                        onChange={formik.handleChange}
                        helpDesc="The homepage URL of your app."
                    />
                </div>
                <AppButton  background="#F2F2F2" sx={{mr: 2}} textColor="#777777" onClick={() => history.replace({pathname: Routes.IntegrationList})}>Cancel</AppButton>
                <AppButton type="submit" disabled={submitting} loading={submitting}>Create</AppButton>
            </form>
        </div>
    );
}
